/**
 * Where to put a label for a zone polygon.
 *
 * Both map platforms need the same answer, for the same reason: a label
 * must not be derived from the polygon itself. Mapbox tiles a polygon
 * internally, and a symbol layer reading that polygon draws one label per
 * tile the shape touches — so a zone spanning five tiles gets five
 * labels. Anchoring the label to a single point removes the problem
 * entirely, and using the same point on web and mobile keeps the two maps
 * looking alike.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Centroid by simple coordinate average. Good enough for ~km-scale zone
 * shapes, which is all this is used for; it is not the area centroid and
 * makes no promise about strongly concave polygons.
 */
export function centroidOf(ring: number[][]): LatLng {
  // Outer ring in GeoJSON is closed — first and last points are equal.
  // Drop the duplicate before averaging so the closing vertex doesn't
  // double-pull the centroid towards itself for nearly-degenerate
  // polygons.
  const trimmed =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring;

  let sumLat = 0;
  let sumLng = 0;
  for (const [lng, lat] of trimmed) {
    sumLat += lat;
    sumLng += lng;
  }
  return {
    lat: sumLat / trimmed.length,
    lng: sumLng / trimmed.length,
  };
}

/**
 * The label anchor for a GeoJSON Polygon's coordinates, as [lng, lat] —
 * the order mapbox wants. Returns null when there is no usable outer ring.
 */
export function polygonLabelPoint(coordinates: number[][][]): [number, number] | null {
  const ring = coordinates?.[0];
  if (!ring || ring.length === 0) return null;

  const { lat, lng } = centroidOf(ring);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lng, lat];
}
